import { useState, useEffect } from 'react';
import { Image, Instagram, X } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent } from '../components/ui/dialog';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API_URL}/photos`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-20 px-6" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Photo <span className="gradient-text">Gallery</span>
          </h1>
          <p className="text-zinc-400 text-lg">
            Moments from the events
          </p>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(12)].map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-xl bg-zinc-800/50" />
            ))}
          </div>
        ) : photos.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <button
                key={photo.id}
                onClick={() => setSelectedPhoto(photo)}
                className="group relative aspect-square rounded-xl overflow-hidden"
                data-testid={`photo-${photo.id}`}
              >
                <img 
                  src={photo.image_url.startsWith('http') ? photo.image_url : `${process.env.REACT_APP_BACKEND_URL}${photo.image_url}`}
                  alt={photo.caption || 'Event photo'}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  {photo.instagram_url && (
                    <Instagram className="w-8 h-8 text-white" />
                  )}
                </div>
                {photo.event_name && (
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-xs text-zinc-300 truncate">{photo.event_name}</p>
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="glass-card rounded-2xl p-16 text-center">
            <Image className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No photos yet</h3>
            <p className="text-zinc-400">Check back after the next event!</p>
          </div>
        )}

        {/* Photo Modal */}
        <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
          <DialogContent className="bg-transparent border-none max-w-4xl p-0">
            {selectedPhoto && (
              <div className="relative">
                <img 
                  src={selectedPhoto.image_url.startsWith('http') ? selectedPhoto.image_url : `${process.env.REACT_APP_BACKEND_URL}${selectedPhoto.image_url}`}
                  alt={selectedPhoto.caption || 'Event photo'}
                  className="w-full h-auto rounded-xl"
                />
                <button
                  onClick={() => setSelectedPhoto(null)}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                {selectedPhoto.instagram_url && (
                  <a
                    href={selectedPhoto.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                  >
                    <Instagram className="w-4 h-4" />
                    View on Instagram
                  </a>
                )}
                {(selectedPhoto.caption || selectedPhoto.title || selectedPhoto.event_name) && (
                  <div className="absolute bottom-4 left-4 p-3 rounded-lg bg-black/50">
                    {(selectedPhoto.caption || selectedPhoto.title) && (
                      <p className="font-bold">{selectedPhoto.caption || selectedPhoto.title}</p>
                    )}
                    {selectedPhoto.event_name && <p className="text-sm text-zinc-400">{selectedPhoto.event_name}</p>}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Gallery;
